import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { supplierOrderChangeAPI } from '../services/supplierOrderChange';
import { shiftTaskHandoverAPI } from '../services/shiftTaskHandover';
import { useAuth } from './AuthProvider';

/**
 * #231533 — Nguồn ĐẾM DUY NHẤT cho những việc đang chờ Admin duyệt.
 *
 * Điều tra #223612: NCC gửi yêu cầu sửa đơn → bản ghi PENDING vào CSDL đúng, màn duyệt
 * `/inventory/supplier-order-changes` cũng có thật, nhưng nó nằm ở mục thứ 4/5 trong submenu
 * "Nhập hàng & Nhà cung cấp" và KHÔNG có gì báo hiệu. Endpoint đếm
 * (`GET /supplier-order-changes/pending-count`) đã có sẵn từ #120207 nhưng chưa nơi nào gọi.
 *
 * Đặt ở context thay vì gọi rời trong từng màn để badge trên sidebar và ô "Cần duyệt" ở trang chủ
 * luôn hiện CÙNG MỘT con số — hai chỗ tự gọi riêng thì duyệt xong sẽ lệch nhau.
 */

const PendingApprovalsContext = createContext(null);

/** 30 giây: đủ nhanh để Admin thấy trong lúc đang mở app, đủ chậm để không thành nguồn tải. */
const POLL_MS = 30000;

export const PendingApprovalsProvider = ({ children }) => {
  const { user } = useAuth();
  const [supplierOrderChanges, setSupplierOrderChanges] = useState(0);
  const [handoverRequests, setHandoverRequests] = useState(0);

  // Endpoint đứng sau `InventoryManagerGuard` (ADMIN/MANAGER/quyền `inventory_manager`). Tài khoản
  // không có quyền sẽ ăn 403 — hỏi lại mỗi 30 giây là spam vô ích, nên hỏng một lần thì tắt hẳn
  // vòng lặp cho phiên đăng nhập đó.
  const disabledRef = useRef(false);
  // Đếm "Bàn giao ca sau" có vòng tắt riêng, để một endpoint hỏng không làm tắt badge kia.
  const handoverDisabledRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    if (!disabledRef.current) {
      try {
        const data = await supplierOrderChangeAPI.pendingCount();
        setSupplierOrderChanges(Number(data?.count ?? 0));
      } catch (err) {
        // Chỉ 401/403 mới là "tài khoản này không được xem" → tắt vòng lặp và xoá badge.
        // Lỗi mạng/5xx chỉ là tạm thời: GIỮ NGUYÊN số cũ, vì hạ badge xuống 0 lúc đó là nói dối
        // ngược hướng an toàn — Admin tưởng hết việc chờ duyệt trong khi vẫn còn.
        if (err?.status === 401 || err?.status === 403) {
          disabledRef.current = true;
          setSupplierOrderChanges(0);
        }
      }
    }
    if (!handoverDisabledRef.current) {
      try {
        const data = await shiftTaskHandoverAPI.list({ status: 'PENDING', limit: 1 });
        setHandoverRequests(Number(data?.pending_count ?? 0));
      } catch (err) {
        if (err?.status === 401 || err?.status === 403) {
          handoverDisabledRef.current = true;
          setHandoverRequests(0);
        }
      }
    }
  }, [user]);

  useEffect(() => {
    // Đăng xuất rồi đăng nhập bằng tài khoản khác: mở lại vòng lặp và đếm lại từ đầu.
    disabledRef.current = false;
    handoverDisabledRef.current = false;
    setSupplierOrderChanges(0);
    setHandoverRequests(0);
    if (!user) return undefined;

    refresh();
    const interval = setInterval(refresh, POLL_MS);
    return () => clearInterval(interval);
  }, [user, refresh]);

  return (
    <PendingApprovalsContext.Provider value={{ supplierOrderChanges, handoverRequests, refresh }}>
      {children}
    </PendingApprovalsContext.Provider>
  );
};

/**
 * Trả về `{ supplierOrderChanges, refresh }`. Không ném lỗi khi dùng ngoài provider — sidebar và
 * trang chủ chỉ mất badge chứ không được trắng màn.
 */
export const usePendingApprovals = () =>
  useContext(PendingApprovalsContext) ?? { supplierOrderChanges: 0, handoverRequests: 0, refresh: () => {} };
